import { apiSlice } from 'api/apiSlice';
import { addObjectToArrayIfNotExist } from 'helpers';
import { optimisticUpdater } from 'api/utils';
import { deleteAchievementInListInQuery } from './util';
// represent the controller
const secondaryUrl = `achievement`;

export const achievementExtendedSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    deleteAchievement: builder.mutation({
      query: ({ achievementId }) => ({
        url: `/v1/${secondaryUrl}/${achievementId}`,
        method: 'DELETE'
      }),
      async onQueryStarted({ achievementId, userId }, { queryFulfilled }) {
        const deleteResult = deleteAchievementInListInQuery({
          achievementId,
          queryName: 'getAchievementsByUserId',
          params: { userId }
        });

        await optimisticUpdater(deleteResult, queryFulfilled);
      },
      invalidatesTags: () => [{ type: 'Achievement', id: 'LIST' }]
    }),
    updateAchievement: builder.mutation({
      query: ({ achievementId, achievement }) => ({
        url: `/v1/${secondaryUrl}/${achievementId}`,
        method: 'PATCH',
        body: achievement
      }),
      // TODO: move this to utils
      async onQueryStarted({ achievementId, achievement, userId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          apiSlice.util.updateQueryData('getAchievementsByUserId', { userId }, (draft) => {
            const item = draft.result.find((item) => item.id === achievementId);
            if (item) Object.assign(item, achievement);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          dispatch(apiSlice.util.invalidateTags([{ type: 'Achievement', id: 'LIST' }]));
        }
      }
    }),

    saveAchievement: builder.mutation({
      query: ({ achievement }) => ({
        url: `/v1/${secondaryUrl}`,
        method: 'POST',
        body: achievement
      }),
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        await optimisticUpdater(
          null,
          queryFulfilled,
          () => {},
          (createdAchievementData) => {
            if (!createdAchievementData) return;
            dispatch(
              apiSlice.util.updateQueryData(
                'getAchievementsByUserId',
                {
                  userId
                },
                (draft) => {
                  draft.result.unshift(createdAchievementData);
                }
              )
            );
          }
        );
      }
    }),

    getAchievementsByUserId: builder.query({
      query: ({ userId, page = 1, size = 3 }) => ({
        url: `/v1/${secondaryUrl}/user/${userId}?page=${page}&size=${size}&sort=from,desc`
      }),
      providesTags: (result) => {
        const mainTags = [{ type: 'Achievement', id: 'LIST' }];
        if (!result) return mainTags;
        let invalidatesTags = result.result.map((item) => ({ type: 'Achievement', id: item.id }));
        invalidatesTags = invalidatesTags.concat(mainTags);
        return invalidatesTags;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { userId } = queryArgs;
        return `${endpointName}({"userId":"${userId}"})`;
      },
      // Always merge incoming data to the cache entry
      merge: (currentCache, newItems) => {
        newItems.result.forEach((item) => {
          // the reason we're using this function is because we want to make sure that the new item is not already in the cache
          // sometimes the cache is not updated properly and we get duplicated items
          // when you create a Achievement, the created Achievement is added to the cache, and the last Achievement
          // is already in the cache, and will be fetched again in the next page, since it will be go 1 page forward
          // when creating a new Achievement
          addObjectToArrayIfNotExist(currentCache.result, item);
        });
        currentCache.page = newItems.page;
        currentCache.links = newItems.links;
      },
      // Refetch when the page arg changes
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      }
    })
  })
});

export const {
  useDeleteAchievementMutation,
  useSaveAchievementMutation,
  useUpdateAchievementMutation,
  useGetAchievementsByUserIdQuery
} = achievementExtendedSlice;
